import { EciuImage } from "@/components/EciuImage";
import { EciuPolygonIcon } from "@/components/EciuPolygons/EciuPolygon";
import { LearningOpportunityCardContainer } from "@/components/LearningOpportunityCard";
import { Markdown } from "@/components/Markdown";
import { Section, SectionContainer } from "@/components/Sections";
import {
  LightWeightLearningOpportunities,
  StatusToIncludeInRecommendationLists,
} from "@/server/backend-api";
import {
  getOrganisation,
  Organisation,
} from "@/server/backend-api/organisation";
import { Assert } from "@/utils/assert";
import { getColorCode } from "@/utils/colors";
import { mcn } from "@/utils/mergeClassNames";
import type {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
  NextPage,
} from "next";
import Link from "next/link";
import { withErrorReportingAndAuthorization } from "../../../middlewares/withErrorReportingAndAuthorisation";
import { GetServerSidePropsResult } from "next/types";
import { getRedirectIfUrlMismatch } from "@/utils/url";
import { Head } from "@/components/Head";

type PageProps = {
  organisation: Organisation;
  learningOpportunities: LightWeightLearningOpportunities[];
};

const getServerSidePropsImpl = async (
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<PageProps>> => {
  const id = context.params?.id;
  Assert.check(typeof id === "string");
  const organisation = await getOrganisation(id, context.req);
  const redirect = getRedirectIfUrlMismatch(context, organisation);
  if (redirect) {
    return redirect;
  }

  const learningOpportunities = [
    ...organisation.learningOpportunitiesAsHost,
    ...organisation.learningOpportunitiesAsPartner,
  ].filter(
    (x) => x.status && StatusToIncludeInRecommendationLists.includes(x.status)
  );

  return {
    props: {
      organisation,
      learningOpportunities,
    },
  };
};

export const getServerSideProps = withErrorReportingAndAuthorization(
  getServerSidePropsImpl
);

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;
const OrganisationPage: NextPage<Props> = ({
  organisation,
  learningOpportunities,
}) => {
  const logoWidthClasses = mcn("w-[230px] desktop:w-[280px]");

  function LogoImage() {
    return (
      <span
        className={mcn(
          logoWidthClasses,
          "h-[230px] desktop:h-[280px]",
          "border-black border-4 border-blue rounded-full"
        )}
      >
        <EciuImage
          objectFit="object-contain"
          className="bg-white"
          src={organisation.logoImageUri}
          rounded={"full"}
        />
      </span>
    );
  }

  function DescriptionSection(props: { className?: string; testId?: string }) {
    return (
      <div
        data-testid={props.testId ?? "description-section"}
        className={mcn(
          "col-span-full tablet:col-span-8 mt-xl tablet:mt-md tablet-lg:mt-2xl laptop:mt-3xl",
          props.className
        )}
      >
        <div className="flex items-center">
          <EciuPolygonIcon
            fillColor={getColorCode("yellow")}
            sizeClassName="w-lg"
          />
          <h6 className="text-style-overline ml-xs">
            {organisation.eciuMemberUniversity
              ? "Member university"
              : "Associate partner"}
          </h6>
        </div>
        <h5 className="text-style-headline2 mt-sm mb-md">
          {organisation.name}
        </h5>
        <Markdown content={description} />
      </div>
    );
  }

  function BgImage({ className }: { className?: string }) {
    return (
      <div className={mcn("col-span-full", className)}>
        <EciuImage
          src={bgImage}
          layout={"responsive"}
          height={720}
          width={1280}
        />
      </div>
    );
  }

  const bgImage = organisation.backgroundImageUri;
  const description = organisation.description ?? "";
  const orgUrl = organisation.homeUrl;
  return (
    <>
      <Head title={organisation.name} description={organisation.description} />
      {bgImage && <BgImage className="tablet-sm:hidden" />}
      <Section
        color="white"
        isFirstSection
        sectionPadding={false}
        fillScreenHeight={false}
      >
        <SectionContainer grid defaultPadding={false}>
          <DescriptionSection
            className="tablet:hidden"
            testId="mobile-description-section"
          />
          {/* Logo Section*/}
          <div className="col-span-full tablet:col-span-4 mb-xl mt-md tablet-lg:mt-2xl laptop:mt-3xl">
            <div className="flex flex-col justify-center items-center">
              <LogoImage />
              {orgUrl && (
                <Link href={orgUrl} target="_blank">
                  <span
                    className={mcn(
                      "h-10",
                      logoWidthClasses,
                      "mt-md flex justify-center items-center bg-light-blue text-white rounded-3 cursor-pointer group"
                    )}
                  >
                    <span className="underline decoration-white underline-offset-2 group-hover:no-underline">
                      To website
                    </span>
                  </span>
                </Link>
              )}
            </div>
          </div>
          {/* Description Section*/}
          <DescriptionSection
            className="hidden tablet:block"
            testId="large-description-section"
          />
          {/* BG Image */}
          {bgImage && <BgImage className="hidden tablet-sm:block" />}
        </SectionContainer>
      </Section>
      {learningOpportunities.length > 0 && (
        <Section sectionPadding={false} className={"mt-2xl"}>
          <LearningOpportunityCardContainer
            heading={`Learning opportunities of ${organisation.name}`}
            headingWithEciuLogo
            items={learningOpportunities}
          />
        </Section>
      )}
    </>
  );
};

export default OrganisationPage;
